import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, BACKEND_URL, fmtDuration } from "@/lib/api";
import { Film, Play, X } from "lucide-react";

/**
 * Mobile Reels — read-only list of built reels with tap-to-play full-screen.
 * Only lists reels that have finished building (status === "done").
 */
export default function MobileReels() {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/reels");
        setReels((r.data || []).filter((x) => x.status === "done"));
      } catch {
        toast.error("Failed to load reels");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="px-4 pt-4" data-testid="mobile-reels-page">
      <div className="flex items-center gap-2 mb-3">
        <Film size={16} className="text-orange-500" />
        <h1 className="font-display uppercase text-lg">Reels</h1>
        <span className="text-[10px] text-stone-500 font-mono-tc ml-1">{reels.length}</span>
      </div>

      {loading ? (
        <div className="text-center text-stone-500 py-16 animate-pulse text-xs uppercase tracking-widest">
          Loading…
        </div>
      ) : reels.length === 0 ? (
        <div className="text-center text-stone-500 py-16 text-xs">
          No reels yet. Build one on desktop and it'll appear here.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 pb-4" data-testid="mobile-reel-list">
          {reels.map((r) => (
            <button
              key={r.id}
              onClick={() => setPreview(r)}
              className="w-full text-left border border-stone-800 hover:border-orange-600/60 bg-stone-950/60 overflow-hidden active:scale-[0.98] transition-transform"
              data-testid={`mobile-reel-${r.id}`}
            >
              <div className="relative aspect-video bg-stone-900">
                {r.thumbnail_s3_key ? (
                  <img
                    src={`${BACKEND_URL}/api/reels/${r.id}/thumbnail`}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-700">
                    <Film size={32} />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="bg-orange-600/90 rounded-full w-14 h-14 flex items-center justify-center">
                    <Play size={20} className="text-white ml-0.5" />
                  </div>
                </div>
                <div className="absolute bottom-1 right-1 bg-black/70 px-1.5 py-0.5 text-[10px] font-mono-tc text-white">
                  {fmtDuration(r.duration || 0)}
                </div>
              </div>
              <div className="p-3">
                <div className="font-medium text-sm truncate">{r.title || "Untitled reel"}</div>
                {r.description && (
                  <div className="text-[11px] text-stone-500 truncate mt-0.5">{r.description}</div>
                )}
                <div className="text-[10px] text-stone-500 mt-1 font-mono-tc">
                  {r.clip_count || (r.clip_ids?.length ?? 0)} clips
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 bg-black z-50 flex flex-col"
          onClick={() => setPreview(null)}
          data-testid="mobile-reel-preview"
        >
          <div className="flex items-center justify-between px-4 py-3 text-stone-100 bg-black/80" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm truncate">{preview.title || "Reel"}</div>
            <button
              onClick={() => setPreview(null)}
              className="text-stone-300 hover:text-white"
              data-testid="mobile-reel-preview-close"
            >
              <X size={20} />
            </button>
          </div>
          <video
            src={`${BACKEND_URL}/api/reels/${preview.id}/stream`}
            controls
            autoPlay
            playsInline
            className="flex-1 w-full bg-black"
            data-testid="mobile-reel-preview-video"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
